import { Check } from 'lucide-react';

import type { ProductPlan } from '@/features/products/types';
import { cn } from '@/lib/utils';
import { perDayPrice, perDayStrike, displayPrice, displayStrikePrice, pricePeriodSuffix } from '../domain/pricing';
import savingsIcon from '../assets/icon-savings.svg';
import priceArrowIcon from '../assets/icon-price-arrow.svg';

interface PlanCardProps {
  plan: ProductPlan;
  selected: boolean;
  onSelect: () => void;
}

function formatAmount(value: number): string {
  return value.toFixed(2);
}

export function PlanCard({ plan, selected, onSelect }: PlanCardProps) {
  const strike = displayStrikePrice(plan);
  const unitPrice = displayPrice(plan);
  const dayPrice = perDayPrice(plan);
  const dayStrike = perDayStrike(plan);
  const periodSuffix = pricePeriodSuffix(plan);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'relative flex w-full flex-col overflow-hidden rounded-2xl bg-white text-left transition-colors',
        selected
          ? 'border-2 border-mf-green'
          : 'border border-mf-neutral-200',
      )}
    >
      {plan.badge && (
        <span className="flex items-center justify-center gap-1 bg-mf-green py-1 text-[10px] font-bold uppercase leading-3 text-white">
          <img src={savingsIcon} alt="" className="h-3 w-2.5" />
          {plan.badge}
        </span>
      )}
      <span className="flex w-full items-center justify-between gap-3 px-4 py-2">
        <span className="flex min-w-0 flex-1 items-center gap-3">
          <span
            className={cn(
              'flex size-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
              selected ? 'border-mf-green bg-mf-green' : 'border-mf-neutral-200',
            )}
          >
            {selected && <Check className="size-3.5 text-white" strokeWidth={3} />}
          </span>
          <span className="flex min-w-0 flex-col gap-0.5">
            <span
              className={cn(
                'text-base font-medium leading-5',
                selected ? 'text-mf-neutral-900' : 'text-mf-neutral-400',
              )}
            >
              {plan.name}
            </span>
            <span className="flex items-center gap-1">
              {strike != null && (
                <>
                  <span className="relative shrink-0 text-sm leading-4 text-mf-neutral-400">
                    {formatAmount(strike)}
                    <span className="absolute inset-x-0 top-1/2 h-px bg-mf-error" />
                  </span>
                  <span className="flex size-2.5 shrink-0 items-center justify-center overflow-hidden">
                    <img src={priceArrowIcon} alt="" className="size-[9px]" />
                  </span>
                </>
              )}
              <span className="flex shrink-0 items-center text-sm leading-4">
                <span className="font-bold text-mf-error">{formatAmount(unitPrice)}</span>
                {periodSuffix ? (
                  <span className="font-normal text-mf-neutral-400">{periodSuffix}</span>
                ) : null}
              </span>
            </span>
          </span>
        </span>
        <span className="flex shrink-0 flex-col items-center whitespace-nowrap">
          {dayStrike != null && (
            <span className="relative text-xs leading-4 text-mf-neutral-400">
              {formatAmount(dayStrike)}
              <span className="absolute inset-x-0 top-1/2 h-px bg-mf-error" />
            </span>
          )}
          <span
            className={cn(
              'font-display text-[28px] font-bold leading-9',
              selected ? 'text-mf-neutral-900' : 'text-mf-neutral-400',
            )}
          >
            {formatAmount(dayPrice)}
          </span>
          <span className="text-xs leading-4 text-mf-neutral-400">per day</span>
        </span>
      </span>
    </button>
  );
}
